import React, { Suspense, useCallback, useEffect, useState } from 'react'
import coreReducer from '../../../../core/utils/reducer'
import store from '../../../../store'
import CoreEnums from '../../../../core/utils/enums'
import ClinicForm from '../components/ClinicsForm'
import ClinicTemplates from '../utils/templates'
import { message, Spin } from 'antd'
import { debounce } from 'lodash'
import { handleError } from '../../../lib/utils'
import { readClinics } from '../utils/utils'
import clinicsCRUDReducer from '../utils/reducer'
import { useDispatch, useSelector } from 'react-redux'

const useClinics = () => {
  const dispatch = useDispatch()
  const viewData = useSelector((state) => state.clinics.data)
  const [loading, setLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    fetchClinics()
    // eslint-disable-next-line
  }, [searchQuery])

  // Improved fetch logic with error handling
  const fetchClinics = async () => {
    const searchTerms = searchQuery.trim().toLowerCase().split(/\s+/)
    // Create a regex pattern string that matches any of the search terms
    const regexPattern = searchTerms.map((term) => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')
    try {
      const filter = {}
      if (searchQuery) {
        filter.$or = [
          {
            $expr: {
              $regexMatch: {
                input: { $toLower: '$name' },
                regex: regexPattern,
                options: 'i'
              }
            }
          },
          {
            $expr: {
              $regexMatch: {
                input: { $toLower: '$physicalAddress' },
                regex: regexPattern,
                options: 'i'
              }
            }
          },
          {
            $expr: {
              $regexMatch: {
                input: { $toLower: '$address' },
                regex: regexPattern,
                options: 'i'
              }
            }
          }
        ]
      }
      const data = await readClinics(filter)
      dispatch(clinicsCRUDReducer.actions.setRecords(data))
    } catch (error) {
      const errorMessage = handleError(error, true)
      message.error({
        content: errorMessage,
        key: 'clinics-fetch-error'
      })
    } finally {
    }
  }

  // More efficient debounced search
  // eslint-disable-next-line
  const handleSearch = useCallback(
    debounce((query) => {
      if (query) {
        setSearchQuery(query)
      } else {
        setSearchQuery('')
      }
    }, 500),
    []
  )

  // Initial load
  useEffect(() => {
    fetchClinics()

    // eslint-disable-next-line
  }, [searchQuery])

  // Memoized form renderer to avoid unnecessary re-renders
  const renderClinicForm = useCallback(
    (data, isNewRecord) => (
      <Suspense fallback={<Spin size='large' tip='Loading form...' />}>
        <ClinicForm data={data} isNewRecord={isNewRecord} refreshView={() => fetchClinics(searchQuery)} />
      </Suspense>
    ),
    // eslint-disable-next-line
    []
  )

  // Create a new clinic record
  const createRecord = useCallback(() => {
    store.dispatch(
      coreReducer.actions.addTab({
        key: `${CoreEnums.tabKeys.CLINICS}_new`,
        closable: true,
        label: 'Register Clinic',
        children: renderClinicForm(ClinicTemplates.dataTemplate(), true)
      })
    )
  }, [renderClinicForm])

  // Edit an existing clinic record
  const editRecord = useCallback(
    (record) => {
      store.dispatch(
        coreReducer.actions.addTab({
          key: `${CoreEnums.tabKeys.CLINICS}_${record._id}`,
          closable: true,
          label: `${record.name}`,
          children: renderClinicForm(record, false)
        })
      )
    },
    [renderClinicForm]
  )

  return {
    refreshView: fetchClinics,
    createRecord,
    handleSearch,
    loading,
    viewData,
    searchQuery,
    editRecord
  }
}

export default useClinics
